/**
 * 新しいパスワード /reset-password?token=（015 → 041）
 *   リンクを開いたら、まず「誰のパスワードか」を GET /auth/reset-password で引いて、メールアドレスを読むだけの欄に出す。
 *   その欄が autocomplete=username なので、Mac／iPhone／Windows／Android の「強いパスワードを作る」が
 *   ユーザー名つきで覚えられる（2026-09-22 射場さん）。期限切れ・使用済みは打つ前に文で分かる
 */
import { MSG, OAUTH_LABEL, OAUTH_MSG, PASSWORD_MSG, Password, passwordProblem, type ChooseResponse, type Me, type ResetLookup } from "@genbaryoku/contract";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { z } from "zod";
import { api, ApiError, messageOf } from "../api.js";
import { setMe } from "../auth.js";
import { AuthCard, ErrorText, Field, Help, PasswordInput, PasswordRules, useFieldIds } from "../components/ui.jsx";

const Form = z.object({ password: Password });
type Form = z.infer<typeof Form>;

export function ResetPasswordPage() {
  const ids = useFieldIds(["mail", "pw"] as const);
  const [sp] = useSearchParams();
  const token = sp.get("token") ?? "";
  const valid = /^[0-9a-f]{64}$/.test(token);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | undefined>();
  const form = useForm<Form>({ resolver: zodResolver(Form), defaultValues: { password: "" }, shouldFocusError: true });
  const lk = useQuery({ queryKey: ["reset-lookup", token], queryFn: () => api<ResetLookup>("GET", `/auth/reset-password?token=${encodeURIComponent(token)}`), enabled: valid, retry: false });
  const email = lk.data?.email ?? "";
  // パスワードの無い人（Google・Apple だけで作った人、#112）：同じリンクで「パスワード設定」
  const setting = lk.data?.hasPassword === false;
  const via = (lk.data?.providers ?? []).map((p) => OAUTH_LABEL[p]).join("・");
  const pwValue = form.watch("password") ?? "";

  const submit = form.handleSubmit(async (v) => {
    setServerError(undefined);
    const weak = passwordProblem(v.password, email);
    if (weak) { form.setError("password", { message: weak }); return; }
    try {
      const res = await api<Me | ChooseResponse>("POST", "/auth/reset-password", { token, password: v.password });
      if ("choose" in res && res.choose) {
        nav("/choose", { replace: true, state: res });   // 会社が2つ以上（040）
        return;
      }
      await setMe(qc, res);
      nav("/", { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.problem.errors?.password) { form.setError("password", { message: String(e.problem.errors.password) }); return; }
      setServerError(messageOf(e));
    }
  });

  const state = !valid ? "invalid" : lk.data?.state;
  if (state === "invalid" || state === "expired" || state === "used") {
    return (
      <AuthCard notices title={MSG.resetTitle}>
        <ErrorText>{state === "expired" ? MSG.tokenExpired : state === "used" ? "このリンクは使用済みです。ログインするか、もう一度やり直してください" : MSG.tokenInvalid}</ErrorText>
        <div className="authlinks">
          <Link className="btn text" to="/forgot">{MSG.resetAgain}</Link>
          <Link className="btn text" to="/login">ログイン</Link>
        </div>
      </AuthCard>
    );
  }

  return (
    // 「？」は題の右（#115、Figma 27:341。PC版・スマホ版で同じ）。#287：題は「パスワード再設定」・欄「新しいパスワード」・ボタン「再設定」
    <AuthCard
      notices
      title={setting ? OAUTH_MSG.resetSetTitle : MSG.resetTitle}
      onSubmit={submit}
      help={<Help testId="help-reset" text={`${setting && via ? `現在は ${via} でログインしています。設定すると、メールアドレスとパスワードでもログインできます。` : ""}設定すると、他の端末のログインはすべて解除されます。${PASSWORD_MSG.tip}`} />}
    >
      <Field id={ids.mail} label="メールアドレス">
        <input id={ids.mail} className="inp ro" type="email" autoComplete="username" value={email} readOnly placeholder={lk.isPending ? "確かめています…" : ""} />
      </Field>
      <Field id={ids.pw} label={MSG.resetField}>
        <PasswordInput id={ids.pw} autoComplete="new-password" reg={form.register("password")} />
        <PasswordRules value={pwValue} email={email} />
      </Field>
      <ErrorText>{serverError ?? form.formState.errors.password?.message}</ErrorText>
      <button type="submit" className="btn primary big" disabled={form.formState.isSubmitting || lk.isPending}>
        {MSG.resetSubmit}
      </button>
    </AuthCard>
  );
}
